import {Injectable} from "@angular/core";
import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import {catchError, EMPTY, map, Observable, throwError} from "rxjs";
import {NotificationService} from "../services/notification.service";
import {AuthService} from "../services/auth.service";
import {AuthStatus} from "../enums/auth-status";

/**
 * Fehlgeschlagene Anfragen schließt der Interceptor nach der Benachrichtigung standardmäßig leer ab (EMPTY). Viele
 * Effects fangen Fehler erst im äußeren pipe ab und wären nach dem ersten Fehler beendet, wenn er durchkäme.
 * Wer auf den Fehler reagieren muss (z. B. mit einer Failure-Action oder einem Ladezustand), setzt dieses Token.
 */
export const PROPAGATE_HTTP_ERRORS = new HttpContextToken<boolean>(() => false);

export function propagateHttpErrors(context = new HttpContext()): HttpContext {
  return context.set(PROPAGATE_HTTP_ERRORS, true);
}

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(private notificationService: NotificationService, private authService: AuthService) {
  }


  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      map(event => {
        if (event instanceof HttpResponse && event.body?.notification) {
          this.notificationService.createNotification({
            title: event.body.notification.title,
            text: event.body.notification.text,
            status: event.body.notification?.type ? event.body.notification?.type : 'message',
            dismissed: 0,
          });

          delete event.body.notification;
          return event;
        }

        return event;
      }),
      catchError((error: HttpErrorResponse) => {
        // error.error ist bei Netzwerkfehlern kein Objekt der API (oder null)
        const apiError = typeof error.error === 'object' && error.error !== null ? error.error : undefined;

        if (this.authService.currentStatus.value === AuthStatus.LOGGED_IN || apiError?.level === 1) {
          this.notificationService.createNotification({
            title: 'Ein Fehler ist aufgetreten',
            text: apiError?.message ?? 'Der Server ist nicht erreichbar. Bitte versuchen Sie es später erneut.',
            status: 'error',
            dismissed: 0,
          });
        }

        return request.context.get(PROPAGATE_HTTP_ERRORS) ? throwError(() => error) : EMPTY;
      })
    );
  }
}
